"use server";

import {
  TAvailability,
  TComment,
  TCompanyEmployee,
  TCompanyInformation,
  TCompare,
  TEducation,
  TExperience,
  TGenericResponse,
  TPayloadEducation,
  TPayloadExperience,
  TPayloadSkill,
  TPayloadLanguage,
  TPayloadSpokenLanguage,
  TPayloadProject,
  TResponseServerAction,
  TSavedVideo,
  TSkill,
  TLanguageSkill,
  TSpokenLanguage,
  TProject,
  TUser,
  TUserAvailability,
  TUserVideo,
  TPayloadFileProject,
} from "./types";
import { api, ErrorResponseData, type AxiosError } from "@/lib/axios";
import { getCookieByName } from "@/lib/cookies";
import { revalidatePath } from "next/cache";
import { format } from "date-fns";

export const getVideosByUserId = async (
  userId: string,
  videoType: string,
): Promise<TResponseServerAction<TUserVideo[]>> => {
  try {
    const response = await api.get<TGenericResponse<TUserVideo[]>>(
      `/profiles/${userId}/videos`,
      {
        params: {
          videoType,
        },
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "Video's Profile fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Video's Profile fetch failed"
          : "Video's Profile fetch failed",
    };
  }
};

export const getMyVideos = async (
  videoType: string,
): Promise<TResponseServerAction<TUserVideo[]>> => {
  try {
    const response = await api.get<TGenericResponse<TUserVideo[]>>(
      `/profiles/me/videos`,
      {
        params: {
          videoType,
        },
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "My Video's Profile fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "My Video's Profile fetch failed"
          : "My Video's Profile fetch failed",
    };
  }
};

export const getProfileById = async (
  userId: string,
): Promise<TResponseServerAction<TUser>> => {
  try {
    const response = await api.get<TGenericResponse<TUser>>(
      `/profiles/${userId}`,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Profile fetch failed"
          : "Profile fetch failed",
    };
  }
};

export const getProfileExperienceById = async (
  userId: string,
): Promise<TResponseServerAction<TExperience[]>> => {
  try {
    const response = await api.get<TGenericResponse<TExperience[]>>(
      `/profiles/${userId}/experiences`,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile experience fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Profile experience fetch failed"
          : "Profile experience fetch failed",
    };
  }
};

export const getProfileEducationById = async (
  userId: string,
): Promise<TResponseServerAction<TEducation[]>> => {
  try {
    const response = await api.get<TGenericResponse<TEducation[]>>(
      `/profiles/${userId}/educations`,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile education fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Profile education fetch failed"
          : "Profile education fetch failed",
    };
  }
};

export const getProfileSkillsById = async (
  userId: string,
): Promise<TResponseServerAction<TSkill[]>> => {
  try {
    const response = await api.get<TGenericResponse<TSkill[]>>(
      `/profiles/${userId}/skills`,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile skills fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Profile skills fetch failed"
          : "Profile skills fetch failed",
    };
  }
};

export const getProfileSupportCommentsById = async (
  userId: string,
): Promise<TResponseServerAction<TComment[]>> => {
  try {
    const response = await api.get<TGenericResponse<TComment[]>>(
      `/profiles/${userId}/support-comments`,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile support comments fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Profile support comments fetch failed"
          : "Profile support comments fetch failed",
    };
  }
};

export const createProfileSupportComments = async (
  userId: string,
  payload: { comment: string },
): Promise<TResponseServerAction<null>> => {
  try {
    await api.post<TGenericResponse<null>>(
      `/profiles/${userId}/support-comments`,
      payload,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    revalidatePath(`/profiles/${userId}`);

    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile support comments created successfully",
      metaData: null,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Profile support comments create failed"
          : "Profile support comments create failed",
    };
  }
};

export const toggleFollowProfileById = async (
  userId: string,
): Promise<TResponseServerAction<null>> => {
  try {
    const response = await api.post<TGenericResponse<null>>(
      `/Profiles/${userId}/togglefollow`,
      {},
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile followed successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Profile follow failed"
          : "Profile follow failed",
    };
  }
};

export const getProfileAvailabilityById = async (
  userId: string,
  date: string,
  timeZone?: string,
): Promise<TResponseServerAction<TUserAvailability[]>> => {
  try {
    const response = await api.get<TGenericResponse<TUserAvailability[]>>(
      `/profiles/${userId}/availability/${date}`,
      {
        params: {
          timeZone,
        },
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile availability fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Profile availability fetch failed"
          : "Profile availability fetch failed",
    };
  }
};

export const getMyProfile = async (): Promise<TResponseServerAction<TUser>> => {
  try {
    const response = await api.get<TGenericResponse<TUser>>(`/profiles/me`, {
      headers: {
        Authorization: await getCookieByName("x-api-key"),
      },
    });

    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Profile fetch failed"
          : "Profile fetch failed",
    };
  }
};

export const getMyExperiences = async (): Promise<
  TResponseServerAction<TExperience[]>
> => {
  try {
    const response = await api.get<TGenericResponse<TExperience[]>>(
      `/profiles/me/experiences`,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile experience fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Profile experience fetch failed"
          : "Profile experience fetch failed",
    };
  }
};

export const addMyExperience = async (
  payload: TPayloadExperience,
): Promise<TResponseServerAction<TExperience>> => {
  try {
    const payloadData = {
      ...payload,
      startOn: format(payload.startOn, "yyyy-MM-dd"),
      endOn: payload.endOn ? format(payload.endOn, "yyyy-MM-dd") : undefined,
    };

    const response = await api.post<TGenericResponse<TExperience>>(
      `/profiles/me/experiences`,
      payloadData,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    revalidatePath("/me");
    revalidatePath("/apply-job/profile");

    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile experience added successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Profile experience add failed"
          : "Profile experience add failed",
    };
  }
};

export const getMyExperienceById = async (
  id: string,
): Promise<TResponseServerAction<TExperience>> => {
  try {
    const response = await api.get<TGenericResponse<TExperience>>(
      `/profiles/me/experiences/${id}`,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile experience fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Profile experience fetch failed"
          : "Profile experience fetch failed",
    };
  }
};

export const updateMyExperience = async (
  experienceId: string,
  payload: TPayloadExperience,
): Promise<TResponseServerAction<TExperience>> => {
  try {
    const payloadData = {
      ...payload,
      startOn: format(payload.startOn, "yyyy-MM-dd"),
      endOn: payload.endOn ? format(payload.endOn, "yyyy-MM-dd") : undefined,
    };

    console.log({ payloadData });
    const response = await api.put<TGenericResponse<TExperience>>(
      `/profiles/me/experiences/${experienceId}`,
      payloadData,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );
    revalidatePath("/me");
    revalidatePath("/apply-job/experience");

    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile experience updated successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    console.log({ axiosError: axiosError.response?.data });

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Profile experience update failed"
          : "Profile experience update failed",
    };
  }
};

export const deleteMyExperience = async (
  id: string,
): Promise<TResponseServerAction<null>> => {
  try {
    await api.delete<TGenericResponse<null>>(`/profiles/me/experiences/${id}`, {
      headers: {
        Authorization: await getCookieByName("x-api-key"),
      },
    });

    revalidatePath("/me");
    revalidatePath("/apply-job/profile");

    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile experience deleted successfully",
      metaData: null,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Profile experience delete failed"
          : "Profile experience delete failed",
    };
  }
};

export const getMyEducations = async (): Promise<
  TResponseServerAction<TEducation[]>
> => {
  try {
    const response = await api.get<TGenericResponse<TEducation[]>>(
      `/profiles/me/educations`,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile education fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Profile education fetch failed"
          : "Profile education fetch failed",
    };
  }
};

export const getMySuggestedUsers = async (
  count: number = 5,
): Promise<TResponseServerAction<TUser[]>> => {
  try {
    const response = await api.get<TGenericResponse<TUser[]>>(
      `/profiles/me/suggested-users`,
      {
        params: {
          count,
        },
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    const raw = response.data?.data;
    const metaData = Array.isArray(raw) ? raw : [];

    return {
      status: "success",
      message: "Suggested Users fetched successfully",
      metaData,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Suggested Users fetch failed"
          : "Suggested Users fetch failed",
    };
  }
};

export const addMyEducation = async (
  payload: TPayloadEducation,
): Promise<TResponseServerAction<TEducation>> => {
  try {
    const payloadData = {
      ...payload,
      startOn: format(payload.startOn, "yyyy-MM-dd"),
      endOn: payload.endOn ? format(payload.endOn, "yyyy-MM-dd") : null,
    };

    const response = await api.post<TGenericResponse<TEducation>>(
      `/profiles/me/educations`,
      payloadData,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    revalidatePath("/me");
    revalidatePath("/apply-job/education-skills-language");
    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile education added successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Profile education add failed"
          : "Profile education add failed",
    };
  }
};

export const getMyEducationById = async (
  id: string,
): Promise<TResponseServerAction<TEducation>> => {
  try {
    const response = await api.get<TGenericResponse<TEducation>>(
      `/profiles/me/educations/${id}`,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile education fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Profile education fetch failed"
          : "Profile education fetch failed",
    };
  }
};

export const updateMyEducation = async (
  educationId: string,
  payload: TPayloadEducation,
): Promise<TResponseServerAction<TEducation>> => {
  try {
    const payloadData = {
      ...payload,
      startOn: format(payload.startOn, "yyyy-MM-dd"),
      endOn: payload.endOn ? format(payload.endOn, "yyyy-MM-dd") : null,
    };

    const response = await api.put<TGenericResponse<TEducation>>(
      `/profiles/me/educations/${educationId}`,
      payloadData,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );
    revalidatePath("/me");
    revalidatePath("/apply-job/profile");

    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile education updated successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    console.log({ axiosError: axiosError.response?.data });

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Profile education update failed"
          : "Profile education update failed",
    };
  }
};

export const deleteMyEducation = async (
  id: string,
): Promise<TResponseServerAction<null>> => {
  try {
    await api.delete<TGenericResponse<null>>(`/profiles/me/educations/${id}`, {
      headers: {
        Authorization: await getCookieByName("x-api-key"),
      },
    });

    revalidatePath("/me");
    revalidatePath("/apply-job/profile");

    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile education deleted successfully",
      metaData: null,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Profile education delete failed"
          : "Profile education delete failed",
    };
  }
};

export const getMySkills = async (): Promise<
  TResponseServerAction<TSkill[]>
> => {
  try {
    const response = await api.get<TGenericResponse<TSkill[]>>(
      `/profiles/me/skills`,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile skills fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Profile skills fetch failed"
          : "Profile skills fetch failed",
    };
  }
};

export const addMySkill = async (
  payload: TPayloadSkill,
): Promise<TResponseServerAction<TSkill>> => {
  try {
    const response = await api.post<TGenericResponse<TSkill>>(
      `/profiles/me/skills`,
      payload,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    revalidatePath("/me");
    revalidatePath("/apply-job/profile");
    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile skill added successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Profile skill add failed"
          : "Profile skill add failed",
    };
  }
};

export const updateMySkill = async (
  skillId: string,
  payload: TPayloadSkill,
): Promise<TResponseServerAction<TSkill>> => {
  try {
    const response = await api.put<TGenericResponse<TSkill>>(
      `/profiles/me/skills/${skillId}`,
      payload,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );
    revalidatePath("/me");
    revalidatePath("/apply-job/profile");

    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile skill updated successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    console.log({ axiosError: axiosError.response?.data });

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Profile skill update failed"
          : "Profile skill update failed",
    };
  }
};

export const deleteMySkill = async (
  id: string,
): Promise<TResponseServerAction<null>> => {
  try {
    await api.delete<TGenericResponse<null>>(`/profiles/me/skills/${id}`, {
      headers: {
        Authorization: await getCookieByName("x-api-key"),
      },
    });

    revalidatePath("/me");
    revalidatePath("/apply-job/profile");

    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile skill deleted successfully",
      metaData: null,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Profile skill delete failed"
          : "Profile skill delete failed",
    };
  }
};

export const getMyLanguages = async (): Promise<
  TResponseServerAction<TLanguageSkill[]>
> => {
  try {
    const response = await api.get<TGenericResponse<TLanguageSkill[]>>(
      `/profiles/me/spokenlanguages`,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile languages fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Profile languages fetch failed"
          : "Profile languages fetch failed",
    };
  }
};

export const getProfileLanguagesById = async (
  userId: string,
): Promise<TResponseServerAction<TLanguageSkill[]>> => {
  try {
    const response = await api.get<TGenericResponse<TLanguageSkill[]>>(
      `/profiles/${userId}/spokenlanguages`,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile languages fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Profile languages fetch failed"
          : "Profile languages fetch failed",
    };
  }
};

export const addMyLanguage = async (
  payload: TPayloadLanguage,
): Promise<TResponseServerAction<TLanguageSkill>> => {
  try {
    const response = await api.post<TGenericResponse<TLanguageSkill>>(
      `/profiles/me/languages`,
      payload,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    revalidatePath("/me");
    revalidatePath("/apply-job/profile");
    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile language added successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Profile language add failed"
          : "Profile language add failed",
    };
  }
};

export const deleteMyLanguage = async (
  id: string,
): Promise<TResponseServerAction<null>> => {
  try {
    await api.delete<TGenericResponse<null>>(`/profiles/me/languages/${id}`, {
      headers: {
        Authorization: await getCookieByName("x-api-key"),
      },
    });

    revalidatePath("/me");
    revalidatePath("/apply-job/education-skills-language");

    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile language deleted successfully",
      metaData: null,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Profile language delete failed"
          : "Profile language delete failed",
    };
  }
};

// SPOKEN LANGUAGES ACTIONS (New API)
export const getMySpokenLanguages = async (): Promise<
  TResponseServerAction<TSpokenLanguage[]>
> => {
  try {
    const response = await api.get<TGenericResponse<TSpokenLanguage[]>>(
      `/profiles/me/spokenlanguages`,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile spoken languages fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Profile spoken languages fetch failed"
          : "Profile spoken languages fetch failed",
    };
  }
};

export const getProfileSpokenLanguagesById = async (
  userId: string,
): Promise<TResponseServerAction<TSpokenLanguage[]>> => {
  try {
    const response = await api.get<TGenericResponse<TSpokenLanguage[]>>(
      `/profiles/${userId}/spokenlanguages`,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile spoken languages fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Profile spoken languages fetch failed"
          : "Profile spoken languages fetch failed",
    };
  }
};

export const addMySpokenLanguage = async (
  payload: TPayloadSpokenLanguage,
): Promise<TResponseServerAction<TSpokenLanguage>> => {
  try {
    const response = await api.post<TGenericResponse<TSpokenLanguage>>(
      `/profiles/me/spokenlanguages`,
      payload,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    revalidatePath("/me");
    revalidatePath("/apply-job/profile");
    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile spoken language added successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Profile spoken language add failed"
          : "Profile spoken language add failed",
    };
  }
};

export const getMySpokenLanguageById = async (
  id: string,
): Promise<TResponseServerAction<TSpokenLanguage>> => {
  try {
    const response = await api.get<TGenericResponse<TSpokenLanguage>>(
      `/profiles/me/spokenlanguages/${id}`,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile spoken language fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Profile spoken language fetch failed"
          : "Profile spoken language fetch failed",
    };
  }
};

export const updateMySpokenLanguage = async (
  spokenlanguageId: string,
  payload: TPayloadSpokenLanguage,
): Promise<TResponseServerAction<TSpokenLanguage>> => {
  try {
    const response = await api.put<TGenericResponse<TSpokenLanguage>>(
      `/profiles/me/spokenlanguages/${spokenlanguageId}`,
      payload,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );
    revalidatePath("/me");
    revalidatePath("/apply-job/profile");

    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile spoken language updated successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    console.log({ axiosError: axiosError.response?.data });

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Profile spoken language update failed"
          : "Profile spoken language update failed",
    };
  }
};

export const deleteMySpokenLanguage = async (
  id: string,
): Promise<TResponseServerAction<null>> => {
  try {
    await api.delete<TGenericResponse<null>>(
      `/profiles/me/spokenlanguages/${id}`,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    revalidatePath("/me");
    revalidatePath("/apply-job/profile");

    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile spoken language deleted successfully",
      metaData: null,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Profile spoken language delete failed"
          : "Profile spoken language delete failed",
    };
  }
};

export const getMyProjects = async (): Promise<
  TResponseServerAction<TProject[]>
> => {
  try {
    const response = await api.get<TGenericResponse<TProject[]>>(
      `/profiles/me/projects`,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile projects fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Profile projects fetch failed"
          : "Profile projects fetch failed",
    };
  }
};

export const getProfileProjectsById = async (
  userId: string,
): Promise<TResponseServerAction<TProject[]>> => {
  try {
    const response = await api.get<TGenericResponse<TProject[]>>(
      `/profiles/${userId}/projects`,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile projects fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Profile projects fetch failed"
          : "Profile projects fetch failed",
    };
  }
};

export const deleteMyProject = async (
  id: string,
): Promise<TResponseServerAction<null>> => {
  try {
    await api.delete<TGenericResponse<null>>(`/profiles/me/projects/${id}`, {
      headers: {
        Authorization: await getCookieByName("x-api-key"),
      },
    });

    revalidatePath("/me");
    revalidatePath("/apply-job/project");

    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile project deleted successfully",
      metaData: null,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Profile project delete failed"
          : "Profile project delete failed",
    };
  }
};

export const uploadFileIntoMyProject = async (
  projectId: string,
  payload: TPayloadFileProject,
): Promise<TResponseServerAction<null>> => {
  try {
    const formData = new FormData();
    formData.append("File", payload.File);
    formData.append("FileType", payload.FileType);
    if (payload.FileType === "Video" && payload.ThumbnailVideo) {
      formData.append("ThumbnailVideo", payload.ThumbnailVideo);
    }

    await api.post<TGenericResponse<null>>(
      `/profiles/me/projects/${projectId}/upload`,
      formData,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    revalidatePath("/apply-job/project");

    return {
      status: "success",
      message: "File added to project successfully.",
      metaData: null,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Job application failed"
          : "Job application failed",
    };
  }
};

export const deleteFileFromMyProject = async (
  projectId: string,
  fileId: string,
): Promise<TResponseServerAction<null>> => {
  try {
    await api.delete<TGenericResponse<null>>(
      `/Profiles/me/projects/${projectId}/file/${fileId}`,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    revalidatePath("/me");
    revalidatePath("/apply-job/project");

    return {
      status: "success",
      message: "File deleted successfully",
      metaData: null,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Failed to delete file"
          : "Failed to delete file",
    };
  }
};

export const addMyProject = async (
  payload: TPayloadProject,
): Promise<TResponseServerAction<TProject>> => {
  try {
    const payloadData = {
      ...payload,
      startOn: format(payload.startOn, "yyyy-MM-dd"),
      endOn: payload.endOn ? format(payload.endOn, "yyyy-MM-dd") : null,
    };

    const response = await api.post<TGenericResponse<TProject>>(
      `/profiles/me/projects`,
      payloadData,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    revalidatePath("/me");
    revalidatePath("/apply-job/profile");
    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile project added successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Profile project add failed"
          : "Profile project add failed",
    };
  }
};

export const getMyProjectById = async (
  id: string,
): Promise<TResponseServerAction<TProject>> => {
  try {
    const response = await api.get<TGenericResponse<TProject>>(
      `/profiles/me/projects/${id}`,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile project fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Profile project fetch failed"
          : "Profile project fetch failed",
    };
  }
};

export const updateMyProject = async (
  projectId: string,
  payload: TPayloadProject,
): Promise<TResponseServerAction<TProject>> => {
  try {
    const payloadData = {
      ...payload,
      startOn: format(payload.startOn, "yyyy-MM-dd"),
      endOn: payload.endOn ? format(payload.endOn, "yyyy-MM-dd") : null,
    };

    const response = await api.put<TGenericResponse<TProject>>(
      `/profiles/me/projects/${projectId}`,
      payloadData,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );
    revalidatePath("/me");
    revalidatePath("/apply-job/profile");

    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile project updated successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    console.log({ axiosError: axiosError.response?.data });

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Profile project update failed"
          : "Profile project update failed",
    };
  }
};

export const getMyPersonalProfileSettings = async (): Promise<
  TResponseServerAction<TUser>
> => {
  try {
    const response = await api.get<TGenericResponse<TUser>>(
      `/profiles/me/personal-profile/settings`,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "Personal profile settings fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Personal profile settings fetch failed"
          : "Personal profile settings fetch failed",
    };
  }
};

export const updateMyPersonalProfileSettings = async (
  payload: TUser,
): Promise<TResponseServerAction<TUser>> => {
  try {
    const response = await api.put<TGenericResponse<TUser>>(
      `/profiles/me/personal-profile/settings`,
      payload,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "Personal profile settings updated successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Personal profile settings update failed"
          : "Personal profile settings update failed",
    };
  }
};

export const updateMyProfilePicture = async (
  formData: FormData,
): Promise<TResponseServerAction<null>> => {
  try {
    const response = await api.post<TGenericResponse<null>>(
      `/profiles/me/upload-profileimage`,
      formData,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    revalidatePath("/me/profile");

    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile picture updated successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Profile picture update failed"
          : "Profile picture update failed",
    };
  }
};

export const getMyAvailability = async ({
  timeZone,
}: {
  timeZone: string;
}): Promise<TResponseServerAction<TAvailability>> => {
  try {
    const response = await api.get<TGenericResponse<TAvailability>>(
      `/profiles/me/availability`,
      {
        params: {
          timeZone,
        },
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "Availability fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    console.log({ axiosError: axiosError.response?.data });

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Availability fetch failed"
          : "Availability fetch failed",
    };
  }
};

export const updateMyAvailability = async (
  payload: TAvailability,
): Promise<TResponseServerAction<TAvailability>> => {
  const response = await api.put<TGenericResponse<TAvailability>>(
    `/profiles/me/availability`,
    payload,
    {
      headers: {
        Authorization: await getCookieByName("x-api-key"),
      },
    },
  );

  // For client components, return success with the data
  return {
    status: "success",
    message: "Availability updated successfully",
    metaData: response.data.data,
  };
};

// START COMPANY ACTIONS
export const getMyCompanyProfileSettings = async (): Promise<
  TResponseServerAction<TUser>
> => {
  try {
    const response = await api.get<TGenericResponse<TUser>>(
      `/profiles/me/company-profile/settings`,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "Company profile settings fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Company profile settings fetch failed"
          : "Company profile settings fetch failed",
    };
  }
};

export const getMyCompanyInformation = async (): Promise<
  TResponseServerAction<TCompanyInformation>
> => {
  try {
    const response = await api.get<TGenericResponse<TCompanyInformation>>(
      `/profiles/me/company-information`,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "Company information fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Company information fetch failed"
          : "Company information fetch failed",
    };
  }
};

export const updateMyCompanyProfileSettings = async (
  payload: TUser,
): Promise<TResponseServerAction<TUser>> => {
  const response = await api.put<TGenericResponse<TUser>>(
    `/profiles/me/company-profile/settings`,
    payload,
    {
      headers: {
        Authorization: await getCookieByName("x-api-key"),
      },
    },
  );

  // For client components, return success with the data
  return {
    status: "success",
    message: "Company profile settings updated successfully",
    metaData: response.data.data,
  };
};

export const getCompanyProfileInformationsById = async (
  userId: string,
): Promise<TResponseServerAction<TCompanyInformation>> => {
  try {
    const response = await api.get<TGenericResponse<TCompanyInformation>>(
      `/profiles/${userId}/company-information`,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile company informations fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Profile company informations fetch failed"
          : "Profile company informations fetch failed",
    };
  }
};

export const getCompanyProfileEmployeesById = async (
  userId: string,
): Promise<TResponseServerAction<TCompanyEmployee[]>> => {
  try {
    const response = await api.get<TGenericResponse<TCompanyEmployee[]>>(
      `/profiles/${userId}/company-employees`,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "Profile company employees fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Profile company employees fetch failed"
          : "Profile company employees fetch failed",
    };
  }
};

export const updateMyCompanyWebsite = async (
  payload: string,
): Promise<TResponseServerAction<string>> => {
  console.log({ payload });

  const response = await api.put<TGenericResponse<string>>(
    `/profiles/me/company-information/website`,
    payload,
    {
      headers: {
        Authorization: await getCookieByName("x-api-key"),
      },
    },
  );

  revalidatePath("/profile/me");

  return {
    status: "success",
    message: "Company website updated successfully",
    metaData: response.data.data,
  };
};

export const updateMyCompanySize = async (
  payload: string,
): Promise<TResponseServerAction<string>> => {
  console.log({ payload });

  const response = await api.put<TGenericResponse<string>>(
    `/profiles/me/company-information/size`,
    payload,
    {
      headers: {
        Authorization: await getCookieByName("x-api-key"),
      },
    },
  );

  revalidatePath("/profile/me");

  // For client components, return success with the data
  return {
    status: "success",
    message: "Company size updated successfully",
    metaData: response.data.data,
  };
};

export const updateMyCompanyHeadquarters = async (
  payload: string,
): Promise<TResponseServerAction<string>> => {
  const response = await api.put<TGenericResponse<string>>(
    `/profiles/me/company-information/headquarters`,
    payload,
    {
      headers: {
        Authorization: await getCookieByName("x-api-key"),
      },
    },
  );

  revalidatePath("/profile/me");

  // For client components, return success with the data
  return {
    status: "success",
    message: "Company headquarters updated successfully",
    metaData: response.data.data,
  };
};

export const updateMyCompanyFounded = async (
  payload: string,
): Promise<TResponseServerAction<string>> => {
  const response = await api.put<TGenericResponse<string>>(
    `/profiles/me/company-information/founded`,
    payload,
    {
      headers: {
        Authorization: await getCookieByName("x-api-key"),
      },
    },
  );

  revalidatePath("/profile/me");

  // For client components, return success with the data
  return {
    status: "success",
    message: "Company founded updated successfully",
    metaData: response.data.data,
  };
};

// END COMPANY ACTIONS

//  START COMPARE ACTIONS
export const getMyCompares = async (): Promise<
  TResponseServerAction<TCompare[]>
> => {
  try {
    const response = await api.get<TGenericResponse<TCompare[]>>(
      `/profiles/me/compare`,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    const raw = response.data?.data;
    const metaData = Array.isArray(raw) ? raw : [];

    return {
      status: "success",
      message: "My Compare Collection fetched successfully",
      metaData,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "My Compare Collection fetch failed"
          : "My Compare Collection fetch failed",
    };
  }
};

export const getMyCompareDetailById = async (
  compareId: string,
  params: {
    orderby: "NewAdd" | "OldAdd" | "Rate";
  },
): Promise<TResponseServerAction<TCompare>> => {
  try {
    const response = await api.get<TGenericResponse<TCompare>>(
      `/profiles/me/compare/${compareId}`,
      {
        params,
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "My Compare Collection fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "My Compare Collection fetch failed"
          : "My Compare Collection fetch failed",
    };
  }
};

export const createCompare = async (payload: {
  jobTitle: string;
}): Promise<TResponseServerAction<TCompare>> => {
  try {
    const res = await api.post<TGenericResponse<TCompare>>(
      `/profiles/me/compare`,
      payload,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // revalidatePath(`/profiles/${userId}`);

    // For client components, return success with the data
    return {
      status: "success",
      message: "Compare collection created successfully",
      metaData: res.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Compare collection create failed"
          : "Compare collection create failed",
    };
  }
};

export const updateCompareByID = async (
  compareId: string,
  payload: { jobTitle: string },
): Promise<TResponseServerAction<TCompare>> => {
  try {
    const res = await api.post<TGenericResponse<TCompare>>(
      `/profiles/me/compare/${compareId}`,
      payload,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // revalidatePath(`/profiles/${userId}`);

    // For client components, return success with the data
    return {
      status: "success",
      message: "Compare collection updated successfully",
      metaData: res.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Compare collection update failed"
          : "Compare collection update failed",
    };
  }
};

export const deleteCompareByID = async (
  compareId: string,
): Promise<TResponseServerAction<null>> => {
  try {
    await api.delete<TGenericResponse<null>>(
      `/profiles/me/compare/${compareId}`,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "Compare collection deleted successfully",
      metaData: null,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Compare collection delete failed"
          : "Compare collection delete failed",
    };
  }
};

export const getCandidateCompareDetailById = async (
  compareId: string,
  userId: string,
): Promise<TResponseServerAction<TCompare>> => {
  try {
    const response = await api.get<TGenericResponse<TCompare>>(
      `/profiles/me/compare/${compareId}/candidate/${userId}`,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "My Candidate Compare Collection fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "My Candidate Compare Collection fetch failed"
          : "My Candidate Compare Collection fetch failed",
    };
  }
};

export const addCandidateIntoCompare = async (
  compareId: string,
  payload: {
    candidateId: string;
    rate: number;
  },
): Promise<TResponseServerAction<TUser>> => {
  try {
    const res = await api.post<TGenericResponse<TUser>>(
      `/profiles/me/compare/${compareId}/candidate`,
      payload,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // revalidatePath(`/profiles/${userId}`);

    // For client components, return success with the data
    return {
      status: "success",
      message: "Compare collection created successfully",
      metaData: res.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Compare collection create failed"
          : "Compare collection create failed",
    };
  }
};

export const updateCandidateCompare = async (
  compareId: string,
  payload: {
    candidateId: string;
    rate: number;
  },
): Promise<TResponseServerAction<TUser>> => {
  try {
    const res = await api.put<TGenericResponse<TUser>>(
      `/profiles/me/compare/${compareId}/candidate`,
      payload,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // revalidatePath(`/profiles/${userId}`);

    // For client components, return success with the data
    return {
      status: "success",
      message: "Compare collection created successfully",
      metaData: res.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Compare collection create failed"
          : "Compare collection create failed",
    };
  }
};

export const deleteCandidateCompare = async (
  compareId: string,
  userId: string,
): Promise<TResponseServerAction<null>> => {
  try {
    await api.delete<TGenericResponse<null>>(
      `/profiles/me/compare/${compareId}/candidate/${userId}`,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // revalidatePath(`/profiles/${userId}`);

    // For client components, return success with the data
    return {
      status: "success",
      message: "Compare collection deleted successfully",
      metaData: null,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Compare collection delete failed"
          : "Compare collection delete failed",
    };
  }
};

//  END COMPARE ACTIONS

export const deleteAccount = async (): Promise<TResponseServerAction<null>> => {
  try {
    await api.delete<TGenericResponse<null>>(`/profiles/me/delete`, {
      headers: {
        Authorization: await getCookieByName("x-api-key"),
      },
    });

    // Logout the user after successful account deletion
    // const { logout } = await import("@/actions/auth");

    // For client components, return success with the data
    return {
      status: "success",
      message:
        "Account has been deactivated. You can reactivate it by logging in within 3 months.",
      metaData: null,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;
    console.log({ axiosError });
    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Failed to delete account. Please try again."
          : "Failed to delete account. Please try again.",
    };
  }
};

export const getMySavedVideos = async (): Promise<
  TResponseServerAction<TSavedVideo[]>
> => {
  try {
    const response = await api.get<TGenericResponse<TSavedVideo[]>>(
      `/profiles/me/saved-videos`,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "My saved videos fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "My saved videos fetch failed"
          : "My saved videos fetch failed",
    };
  }
};

export const revalidateProjectPath = async () => {
  revalidatePath("/apply-job/project");
};
